import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationSkippedCode,
  NavigationStart,
  Router
} from '@angular/router';

import { APP_ROUTE } from 'app/app-routing.constant';
import { RouterService } from 'app/core/navigation';
import { PARENTS_ROUTE } from 'app/parents/parents-routing.constant';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { StudentService } from 'app/student/services/student.service';
import { TEACHERS_ROUTE } from 'app/teachers/teachers-routing.constant';
import { AuthenticationService, PermissionsService, UserRole } from '../../authentication';
import { DEFAULT_PAGE_ROLE_MAPPING, NAVIGATION_URLS, PageRoleMapping } from '../constants';

type LandingAttemptResult = 'landed' | 'failed' | 'superseded';

type NavigationLifecycleEvent = NavigationStart | NavigationEnd | NavigationCancel | NavigationError | NavigationSkipped;

const NOT_FOUND_ROUTE_PATHS = new Set<string>(['**', APP_ROUTE.PAGE_NOT_FOUND.name, STUDENT_ROUTE.PAGE_NOT_FOUND.name]);

@Injectable({
  providedIn: 'root'
})
export class DefaultPageRouteService {
  private previousRedirectKey = 'kh_previous_redirect';

  private readonly authService = inject(AuthenticationService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly routerService = inject(RouterService);
  private readonly studentService = inject(StudentService);

  public async routeToDefaultPage(): Promise<void> {
    const roles = this.authService.roles();

    if (roles.includes(UserRole.viewOnlyParent)) {
      this.authService.logOut();
      this.redirectBrowser(NAVIGATION_URLS.VIEW_ONLY_PARENT_URL);
    }

    for (const landing of this.getLandingCandidates(roles)) {
      const result = await this.attemptLanding(landing.state);

      // 'superseded' means a guard or another caller started its own navigation; let it win.
      if (result !== 'failed') {
        return;
      }
    }

    await this.navigateToForbidden();
  }

  public async routeToDefaultSelectedRole(role: string): Promise<void> {
    if (role && Object.values(UserRole).includes(role as UserRole)) {
      const roleMapping = DEFAULT_PAGE_ROLE_MAPPING[role as UserRole];

      await this.router.navigate(roleMapping.state);
    }
  }

  public getCurrentFallbackPage(): string {
    if (this.permissionsService.isInRole(UserRole.student)) {
      return NAVIGATION_URLS.STUDENT_FALLBACK_PAGE;
    }

    return NAVIGATION_URLS.PUBLIC_FALLBACK_PAGE;
  }

  public async navigateToPublicFallback(): Promise<void> {
    const interruptedUrl = this.authService.getInterruptedUrl();
    const previousRedirect = localStorage.getItem(this.previousRedirectKey);
    const fallbackUrl = this.getCurrentFallbackPage();

    if (interruptedUrl.split('?')[0].toLowerCase() === fallbackUrl.toLowerCase()) {
      this.authService.setInterruptedUrl('');
      location.href = location.href.split('?')[0];
      localStorage.removeItem(this.previousRedirectKey);
      await this.router.navigateByUrl(fallbackUrl, { replaceUrl: true });

      return;
    }

    if (interruptedUrl && previousRedirect !== interruptedUrl) {
      localStorage.setItem(this.previousRedirectKey, interruptedUrl);
      await this.router.navigateByUrl(`${fallbackUrl}?RedirectFromUrl=${interruptedUrl}`, { replaceUrl: true });
    }
    else {
      localStorage.setItem(this.previousRedirectKey, '');
      await this.router.navigateByUrl(fallbackUrl, { replaceUrl: true });
    }
  }

  public async navigateToForbidden(): Promise<void> {
    localStorage.removeItem(this.previousRedirectKey);
    await this.router.navigateByUrl(NAVIGATION_URLS.FORBIDDEN_FALLBACK_PAGE);
  }

  public async navigateToSettings(): Promise<void> {
    const roles = this.authService.roles();
    const queryParams = { show: 'EmailNotification' };
    if (roles.includes(UserRole.parent)) {
      await this.routerService.navigate(PARENTS_ROUTE.SETTINGS.route(), { queryParams });

      return;
    }

    if (roles.includes(UserRole.teacher)) {
      await this.routerService.navigate(TEACHERS_ROUTE.SETTINGS.route(), { queryParams });

      return;
    }

    if (roles.includes(UserRole.student)) {
      await this.routerService.navigate(STUDENT_ROUTE.SETTINGS.route(this.studentService.activeClassId), { queryParams });

      return;
    }

    await this.routeToDefaultPage();
  }

  /**
   * Landing pages for the user's recognised roles, ordered by DEFAULT_PAGE_ROLE_MAPPING priority
   * (ascending). Roles sharing a landing state are only tried once.
   */
  private getLandingCandidates(roles: string[]): PageRoleMapping[] {
    const knownRoles = new Set<string>(Object.values(UserRole));
    const seenStates = new Set<string>();

    return roles
      .filter((role): role is UserRole => knownRoles.has(role))
      .map((role) => DEFAULT_PAGE_ROLE_MAPPING[role])
      .sort((a, b) => a.priority - b.priority)
      .filter((mapping) => {
        const key = mapping.state.join('/');
        if (seenStates.has(key)) {
          return false;
        }
        seenStates.add(key);

        return true;
      });
  }

  /**
   * Navigates to a landing state and reports whether the user actually ended up on a usable page.
   * Lazy-loaded routes are not in router.config until loaded, so this relies on the navigation
   * outcome and the final activated route rather than inspecting the config up front.
   */
  private async attemptLanding(state: string[]): Promise<LandingAttemptResult> {
    const events: NavigationLifecycleEvent[] = [];
    const subscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart
        || event instanceof NavigationEnd
        || event instanceof NavigationCancel
        || event instanceof NavigationError
        || event instanceof NavigationSkipped) {
        events.push(event);
      }
    });

    let navigated: boolean;
    try {
      navigated = await this.router.navigate(state);
    }
    catch (error: unknown) {
      console.error(error);

      return 'failed';
    }
    finally {
      subscription.unsubscribe();
    }

    if (navigated) {
      return this.isOnNotFoundPage() ? 'failed' : 'landed';
    }

    const outcome = this.getFinalNavigationEvent(events);

    if (outcome instanceof NavigationCancel && outcome.code === NavigationCancellationCode.SupersededByNewNavigation) {
      return 'superseded';
    }

    if (outcome instanceof NavigationSkipped && outcome.code === NavigationSkippedCode.IgnoredSameUrlNavigation) {
      // Already on this landing page.
      return this.isOnNotFoundPage() ? 'failed' : 'landed';
    }

    return 'failed';
  }

  /**
   * Follows our navigation through any guard/redirect UrlTree hops (which share the original
   * navigation promise) and returns the event that terminated it.
   */
  private getFinalNavigationEvent(events: NavigationLifecycleEvent[]): NavigationLifecycleEvent | undefined {
    let currentId: number | undefined;
    let awaitingStart = true;
    let final: NavigationLifecycleEvent | undefined;

    for (const event of events) {
      if (event instanceof NavigationStart) {
        if (awaitingStart) {
          currentId = event.id;
          awaitingStart = false;
          final = undefined;
        }
        continue;
      }

      if (event.id !== currentId) {
        continue;
      }

      if (event instanceof NavigationCancel && event.code === NavigationCancellationCode.Redirect) {
        awaitingStart = true;
        continue;
      }

      final = event;
    }

    return final;
  }

  private isOnNotFoundPage(): boolean {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route?.firstChild) {
      route = route.firstChild;
    }

    const path = route?.routeConfig?.path;

    return path !== undefined && NOT_FOUND_ROUTE_PATHS.has(path);
  }

  private redirectBrowser(url: string): void {
    window.location.href = url;
  }
}
